import { FC, lazy, Suspense, useContext, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom'
import DefaultLayout, { ILink } from '@/components/layouts/DefaultLayout';
import { AuthenticatedUserContext, ClientConfigurationContext } from '@ngt/openiddict';

import ChangePassword from '@/pages/ChangePassword';
import ForgotPassword from '@/pages/ForgotPassword';
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Logout from '@/pages/Logout';
import SetPassword from '@/pages/SetPassword';
import ErrorPage from '@/pages/Error';
import Redirect from '@/pages/Redirect';


import Administration from '@/pages/administration/Administration';
import Application from '@/pages/administration/Application';
import Applications from '@/pages/administration/Applications';
import Authorization from '@/pages/administration/Authorization';
import Authorizations from '@/pages/administration/Authorizations';
import Scope from '@/pages/administration/Scope';
import Scopes from '@/pages/administration/Scopes';
import Token from '@/pages/administration/Token';
import Tokens from '@/pages/administration/Tokens';
import { faBinoculars, faBrowser, faHomeAlt, faTicket, faUserShield } from '@fortawesome/pro-duotone-svg-icons';
import RouteErrorBoundary from './components/errors/RouteErrorBoundary';
import useSentryUser from './hooks/sentry/useSentryUser';
import * as Sentry from "@sentry/react";


const Test: FC<{test: string;}> = ({ test }) => {
    throw new Error("Error McErrorFace");
    return (
        <>
            {test}
            <Outlet />
        </>
    );
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);


const App: FC = () => {
    useSentryUser();
    const {isAdmin, hasAuth} = useContext(AuthenticatedUserContext);
    const { data: clientConfiguration } = useContext(ClientConfigurationContext);

    const location = useLocation();

    const currentLocation = useMemo(() => `${location.pathname}${location.search}${location.hash}`, [location]);

    const adminLinks: ILink[] = useMemo(() => [
        {
            icon: faHomeAlt,
            newTab: false,
            route: true,
            title: 'Administration',
            url: clientConfiguration?.administrationUris?.administration ?? '/'
        },
        {
            icon: faBrowser,
            newTab: false,
            route: true,
            title: 'Applications',
            url: clientConfiguration?.administrationUris?.applications ?? '/'
        },
        {
            icon: faUserShield,
            newTab: false,
            route: true,
            title: 'Authorizations',
            url: clientConfiguration?.administrationUris?.authorizations ?? '/'
        },
        {
            icon: faBinoculars,
            newTab: false,
            route: true,
            title: 'Scopes',
            url: clientConfiguration?.administrationUris?.scopes ?? '/'
        },
        {
            icon: faTicket,
            newTab: false,
            route: true,
            title: 'Tokens',
            url: clientConfiguration?.administrationUris?.tokens ?? '/'
        }
    ], [clientConfiguration]);

    return (
        <>
            <Suspense>
                <SentryRoutes>
                    {
                        isAdmin && (
                            <Route 
                                path="/administration" 
                                element={<DefaultLayout links={adminLinks} />}
                                ErrorBoundary={RouteErrorBoundary}
                            >
                                <Route path={clientConfiguration?.administrationUris?.administration} element={<Administration />} />
                                <Route path={clientConfiguration?.administrationUris?.application} element={<Application />} />
                                <Route path={clientConfiguration?.administrationUris?.applications} element={<Applications />} />
                                <Route path={clientConfiguration?.administrationUris?.authorization} element={<Authorization />} />
                                <Route path={clientConfiguration?.administrationUris?.authorizations} element={<Authorizations />} />
                                <Route path={clientConfiguration?.administrationUris?.scope} element={<Scope />} />
                                <Route path={clientConfiguration?.administrationUris?.scopes} element={<Scopes />} />
                                <Route path={clientConfiguration?.administrationUris?.token} element={<Token />} />
                                <Route path={clientConfiguration?.administrationUris?.tokens} element={<Tokens />} />
                                <Route path="*" element={<Navigate to={`${clientConfiguration?.uris?.error}?code=404`} replace />} />
                            </Route>
                        )
                    }
                    {
                        hasAuth && !isAdmin && (
                            <Route 
                                path="/administration" 
                                element={<DefaultLayout links={adminLinks} />}
                                ErrorBoundary={RouteErrorBoundary}
                            >
                                <Route path="*" element={<Navigate to={`${clientConfiguration?.uris?.error}?code=403`} replace />} />
                            </Route>
                        )
                    }
                    {
                        !hasAuth && (
                            <Route 
                                path="/administration" 
                                element={<DefaultLayout links={adminLinks} />}
                                ErrorBoundary={RouteErrorBoundary}
                            >
                                <Route path="*" element={<Navigate to={`${clientConfiguration?.uris?.login}?ReturnUrl=${encodeURIComponent(currentLocation)}`} replace />} />
                            </Route>
                        )
                    }
                    <Route 
                        path="/" 
                        element={<DefaultLayout />}
                        ErrorBoundary={RouteErrorBoundary}
                    >
                        {!hasAuth && <Route index element={<Navigate to={`${clientConfiguration?.uris?.login}?ReturnUrl=${encodeURIComponent(currentLocation)}`} replace />} />}
                        {hasAuth && <Route index element={<Home />} />}

                        {!hasAuth && <Route path={clientConfiguration?.uris?.login} element={<Login />} />}
                        {hasAuth && <Route path={clientConfiguration?.uris?.login} element={<Navigate to="/" replace />} />}

                        {hasAuth && <Route path={clientConfiguration?.uris?.logout} element={<Logout />} />}
                        {!hasAuth && <Route path={clientConfiguration?.uris?.logout} element={<Navigate to="/" replace />} />}

                        {!hasAuth && <Route path={clientConfiguration?.uris?.forgotPassword} element={<ForgotPassword />} />}
                        {hasAuth && <Route path={clientConfiguration?.uris?.forgotPassword} element={<Navigate to="/" replace />} />}

                        {!hasAuth && <Route path={clientConfiguration?.uris?.setPassword} element={<SetPassword />} />}
                        {hasAuth && <Route path={clientConfiguration?.uris?.setPassword} element={<Navigate to="/" replace />} />}

                        {hasAuth && <Route path={clientConfiguration?.uris?.password} element={<ChangePassword />} />}
                        {!hasAuth && <Route path={clientConfiguration?.uris?.password} element={<Navigate to={`${clientConfiguration?.uris?.login}?ReturnUrl=${encodeURIComponent(currentLocation)}`} replace />} />}
                        
                        <Route path={clientConfiguration?.uris?.error} element={<ErrorPage />} />

                        {/* <Route path="*" element={<Navigate to={`${clientConfiguration?.uris.error}?code=404`} replace />} /> */}
                    </Route>
                    <Route path="/redirect" element={<Redirect />} />
                </SentryRoutes>
            </Suspense>
        </>
    )
}

export default App
